import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useRouter, useRootNavigationState } from 'expo-router';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { useCreateAccount } from '@/hooks/useCreateAccount';
import { signOut } from '@/services/auth';
import { authStateResolvedAtom, userIdAtom } from '@/services/globalState';
import { getPlatformStorage } from '@/util/platformStorage';

/**
 * Logout redirect handler (/logout)
 *
 * Handles logout for all auth methods. OAuth web users get redirected here by Cognito Hosted UI
 * after logout (userId is already undefined). Email/password and mobile users navigate here
 * directly and need signOut() called. Redirects to landing page after logout completes.
 *
 * OAuth web sessions are tracked via sessionStorage to distinguish from email/password users.
 * On logout, OAuth web users rely on Hosted UI redirect (no manual redirect needed). Email/password
 * and mobile users always redirect manually after signOut() completes.
 */
export default function Logout() {
  const router = useRouter();

  const rootNav = useRootNavigationState();

  const authStateResolved = useAtomValue(authStateResolvedAtom);

  const userId = useAtomValue(userIdAtom);

  const { reset: resetCreateAccount } = useCreateAccount();

  const hasRedirectedRef = useRef(false);

  const [status, setStatus] = useState<'idle' | 'working'>('idle');

  const clearSignupState = async () => {
    resetCreateAccount();

    try {
      const storage = getPlatformStorage(undefined, 'local');

      await storage.removeItem('primum_referral_code');
    } catch {}
  };

  useEffect(() => {
    if (!authStateResolved) {
      return;
    }

    if (!rootNav?.key) {
      // wait for navigator to mount
      return;
    }

    if (hasRedirectedRef.current) {
      return;
    }

    hasRedirectedRef.current = true;

    const isFromHostedUIRedirect = !userId && isWeb;

    if (isFromHostedUIRedirect) {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.removeItem('primum_oauth_session');
        }
      } catch {}

      clearSignupState();

      router.replace(ROUTES.DIRECTORY);

      return;
    }

    (async () => {
      setStatus('working');

      try {
        await signOut();
      } catch (err) {
        console.error('[Logout] SignOut error:', err);
      }

      await clearSignupState();

      try {
        const shouldRelyOnHostedUi =
          typeof window !== 'undefined' && !!window.sessionStorage?.getItem('primum_oauth_session');

        if (isWeb && shouldRelyOnHostedUi) {
          return;
        }
      } catch {}

      const redirectPath = isWeb ? ROUTES.DIRECTORY : '/';

      router.replace(redirectPath);
    })();
  }, [router, authStateResolved, userId, rootNav?.key]);

  if (status === 'working') {
    return (
      <VStack className="flex-1 items-center justify-center gap-6 p-6">
        <Spinner size="large" />
        <Text className="text-center font-semibold text-xl">Signing out…</Text>
      </VStack>
    );
  }

  return null;
}
